import { ProCard } from '@ant-design/pro-components';
import {
  Button,
  Divider,
  Input,
  Modal,
  message,
  Space,
  Tag,
  Typography,
} from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { formatCrmDate } from '@/modules/zy-crm/utils/display';
import {
  recalculateOpportunityAiScore,
  submitOpportunityAiScoreFeedback,
} from '@/services/bff/opportunity.api';
import type {
  OpportunityAiScoreReason,
  OpportunityItem,
} from '@/services/bff/types/opportunity';

const EMPTY_REASON: OpportunityAiScoreReason = {
  positiveFactors: [],
  riskFactors: [],
  missingInformation: [],
};

export const parseOpportunityAiScoreReason = (
  raw?: string,
): OpportunityAiScoreReason => {
  try {
    const value = JSON.parse(raw || '{}') as Partial<OpportunityAiScoreReason>;
    return {
      positiveFactors: Array.isArray(value.positiveFactors)
        ? value.positiveFactors.map(String)
        : [],
      riskFactors: Array.isArray(value.riskFactors)
        ? value.riskFactors.map(String)
        : [],
      missingInformation: Array.isArray(value.missingInformation)
        ? value.missingInformation.map(String)
        : [],
      suggestion:
        typeof value.suggestion === 'string' ? value.suggestion : undefined,
    };
  } catch {
    return EMPTY_REASON;
  }
};

type OpportunityAiScorePanelProps = {
  opportunity: OpportunityItem;
  canManage?: boolean;
  onUpdated?: (opportunity: OpportunityItem) => void | Promise<void>;
};

export function OpportunityAiScorePanel({
  opportunity,
  canManage = false,
  onUpdated,
}: OpportunityAiScorePanelProps) {
  const [current, setCurrent] = useState(opportunity);
  const [recalculating, setRecalculating] = useState(false);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackReason, setFeedbackReason] = useState('');

  useEffect(() => setCurrent(opportunity), [opportunity]);
  const reason = useMemo(
    () => parseOpportunityAiScoreReason(current.aiScoreReasonJson),
    [current.aiScoreReasonJson],
  );

  const recalculate = async () => {
    setRecalculating(true);
    try {
      const next = await recalculateOpportunityAiScore(current.uuid);
      setCurrent(next);
      message.success('AI 评分已更新');
      await onUpdated?.(next);
    } catch (error) {
      message.error(
        error instanceof Error
          ? `重新评分失败：${error.message}`
          : '重新评分失败，已保留原评分',
      );
    } finally {
      setRecalculating(false);
    }
  };

  const submitFeedback = async (
    verdict: 'accurate' | 'inaccurate',
    feedback?: string,
  ) => {
    if (!current.aiScoreRunUuid) {
      message.warning('当前评分没有可反馈的评分批次');
      return;
    }
    setFeedbackLoading(true);
    try {
      await submitOpportunityAiScoreFeedback(current.uuid, {
        scoreRunUuid: current.aiScoreRunUuid,
        verdict,
        reason: feedback?.trim() || undefined,
      });
      message.success('感谢反馈');
      setFeedbackOpen(false);
      setFeedbackReason('');
    } finally {
      setFeedbackLoading(false);
    }
  };

  const renderFactors = (title: string, values: string[], color: string) =>
    values.length ? (
      <div>
        <Typography.Text type="secondary">{title}</Typography.Text>
        <div style={{ marginTop: 6 }}>
          <Space size={[4, 6]} wrap>
            {values.map((value) => (
              <Tag key={`${title}-${value}`} color={color}>
                {value}
              </Tag>
            ))}
          </Space>
        </div>
      </div>
    ) : null;

  return (
    <>
      <ProCard
        title="AI 商机评分"
        extra={
          canManage ? (
            <Button loading={recalculating} onClick={() => void recalculate()}>
              重新评分
            </Button>
          ) : null
        }
      >
        <Space align="start" size={20}>
          <Typography.Title
            level={1}
            style={{
              color: 'var(--ant-color-primary)',
              lineHeight: 1,
              margin: 0,
            }}
          >
            {current.aiScore ?? '-'}
          </Typography.Title>
          <div>
            <Space size={6}>
              <Typography.Text strong>
                {current.aiScoreSummary || '等待 AI 完成评分'}
              </Typography.Text>
              {current.aiScoreStale ? <Tag color="orange">待更新</Tag> : null}
            </Space>
            <div>
              <Typography.Text type="secondary">
                更新时间：{formatCrmDate(current.aiScoreUpdatedAt)}
              </Typography.Text>
            </div>
          </div>
        </Space>

        {reason.suggestion ? (
          <Typography.Paragraph style={{ marginTop: 14, marginBottom: 10 }}>
            建议：{reason.suggestion}
          </Typography.Paragraph>
        ) : null}
        <Space orientation="vertical" size={10} style={{ width: '100%' }}>
          {renderFactors('正向因素', reason.positiveFactors, 'green')}
          {renderFactors('风险因素', reason.riskFactors, 'orange')}
          {renderFactors('信息缺口', reason.missingInformation, 'default')}
        </Space>

        {current.aiScoreRunUuid ? (
          <>
            <Divider style={{ margin: '16px 0 12px' }} />
            <Space>
              <Typography.Text type="secondary">
                这个判断准确吗？
              </Typography.Text>
              <Button
                size="small"
                loading={feedbackLoading}
                onClick={() => void submitFeedback('accurate')}
              >
                准确
              </Button>
              <Button size="small" onClick={() => setFeedbackOpen(true)}>
                不准确
              </Button>
            </Space>
          </>
        ) : null}
      </ProCard>

      <Modal
        closable={{ 'aria-label': '关闭' }}
        keyboard
        title="告诉我们哪里不准确"
        open={feedbackOpen}
        confirmLoading={feedbackLoading}
        onOk={() => void submitFeedback('inaccurate', feedbackReason)}
        onCancel={() => setFeedbackOpen(false)}
      >
        <Input.TextArea
          value={feedbackReason}
          maxLength={500}
          showCount
          rows={4}
          placeholder="可选：补充实际情况，帮助后续评分更准确"
          onChange={(event) => setFeedbackReason(event.target.value)}
        />
      </Modal>
    </>
  );
}

export default OpportunityAiScorePanel;
